import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, Calendar, MapPin, Users, Eye, Clock, Ticket, Search, Filter, Upload, Camera } from 'lucide-react';
import { eventsApi } from '../../services/eventsApi';
import { imageAPI, imageUtils } from '../../services/imageApi';
import { isManagedMediaUrl } from '../../utils/mediaImageUtils';
import {
  ADMIN_ICON_STROKE,
  AdminPageHeader,
  AdminStatGrid,
  AdminToolbarIcon,
  AdminTableIconButton,
  AdminTableIconButtonDanger,
  AdminPaginationBar,
  paginateArray,
  DEFAULT_ADMIN_PAGE_SIZE
} from '../../components/admin/AdminShared';
import DraggableStayImagesGrid from '../../components/DraggableStayImagesGrid';
import { roomTaxesFromDiscounted } from '../../utils/roomPricing';

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShowsModal, setShowShowsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [listPage, setListPage] = useState(1);

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsApi.getAllEvents({ limit: 100 });
      setEvents(response.events || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return events.filter((event) => {
      const hay = [event.title, event.city, event.state, event.venue, event.eventId]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !q || hay.includes(q);
      const matchesCategory = filterCategory === 'all' || event.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [events, searchTerm, filterCategory]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, filterCategory]);

  const { pageItems: pagedEvents, total: filteredEventTotal, page: safeEventPage } = useMemo(
    () => paginateArray(filteredEvents, listPage, DEFAULT_ADMIN_PAGE_SIZE),
    [filteredEvents, listPage]
  );

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setShowCreateModal(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      try {
        await eventsApi.deleteEvent(eventId);
        alert('Event deleted successfully!');
        fetchEvents();
      } catch (err) {
        console.error('Error deleting event:', err);
        const errorMessage = err.response?.data?.error || 'Failed to delete event';
        alert(errorMessage);
      }
    }
  };

  const handleManageShows = (event) => {
    setSelectedEvent(event);
    setShowShowsModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (event) => {
    const now = new Date();
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    if (endDate < now) return 'bg-gray-100 text-gray-800';
    if (startDate <= now && endDate >= now) return 'bg-green-100 text-green-800';
    return 'bg-[#FAF7F2] text-[#5c5c5c]';
  };

  const getStatusText = (event) => {
    const now = new Date();
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    if (endDate < now) return 'Completed';
    if (startDate <= now && endDate >= now) return 'Active';
    return 'Upcoming';
  };

  const eventStatItems = useMemo(() => {
    const now = new Date();
    const activeCount = events.filter((event) => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      return start <= now && end >= now;
    }).length;
    const upcomingCount = events.filter((event) => new Date(event.startDate) > now).length;
    const spotlightCount = events.filter((event) => event.inSpotlight).length;
    return [
      { label: 'Total events', value: events.length, icon: Calendar },
      { label: 'Active now', value: activeCount, icon: Users },
      { label: 'Upcoming', value: upcomingCount, icon: Clock },
      { label: 'In spotlight', value: spotlightCount, icon: Eye }
    ];
  }, [events]);

  if (loading) {
    return (
      <div className="py-4">
        <div className="animate-pulse">
          <div className="h-8 bg-[#E5E3DF] rounded w-1/4 mb-6 max-w-xs" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-[#FAF7F2] border border-[#E5E3DF] rounded-[16px]" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader title="Events" subtitle="Manage events, shows, and ticket types">
        <button
          type="button"
          onClick={handleCreateEvent}
          className="bg-[#FF5E4C] text-white px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:opacity-90 transition-opacity text-[14px] font-medium"
        >
          <Plus className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
          Create Event
        </button>
      </AdminPageHeader>

      {/* Filters */}
      <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-4 md:p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <AdminToolbarIcon>
              <Search className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
            </AdminToolbarIcon>
            <input
              type="text"
              placeholder="Search events by name or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full min-w-0 px-4 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
            />
          </div>
          <div className="sm:w-48 flex items-center gap-2">
            <AdminToolbarIcon>
              <Filter className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
            </AdminToolbarIcon>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
            >
              <option value="all">All Categories</option>
              <option value="workshop">Workshop</option>
              <option value="show">Show</option>
              <option value="competition">Competition</option>
              <option value="social">Social</option>
              <option value="training">Training</option>
              <option value="outdoor">Outdoor</option>
              <option value="indoor">Indoor</option>
            </select>
          </div>
        </div>
      </div>

      <AdminStatGrid items={eventStatItems} loading={false} />

      {/* Events Table */}
      <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#FAF7F2]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || filterCategory !== 'all' ? 'No events match your filters' : 'No events found'}
                  </td>
                </tr>
              ) : (
                pagedEvents.map((event) => (
                  <tr key={event.eventId} className="hover:bg-[#FAF7F2]/60 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={event.images?.[0] || '/api/placeholder/48/48'}
                            alt={event.title}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{event.title}</div>
                          <div className="text-sm text-gray-500">{event.venue}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 capitalize">
                        {event.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{formatDate(event.startDate)}</div>
                      <div className="text-gray-500">to {formatDate(event.endDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <MapPin className="mr-1 w-4 h-4 text-[#78736A] shrink-0" strokeWidth={ADMIN_ICON_STROKE} />
                        {event.city}, {event.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event)}`}>
                        {getStatusText(event)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Ticket className="mr-1 w-4 h-4 text-[#78736A] shrink-0" strokeWidth={ADMIN_ICON_STROKE} />
                        {event.shows?.length || 0} shows
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleManageShows(event)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FAF7F2] text-[#FF5E4C] hover:opacity-90 rounded-full text-[13px] font-medium transition-opacity"
                          title="Manage Shows & Tickets"
                          type="button"
                        >
                          <Ticket className="w-4 h-4 shrink-0" strokeWidth={ADMIN_ICON_STROKE} />
                          <span>Shows</span>
                        </button>
                        <AdminTableIconButton title="Edit event" onClick={() => handleEditEvent(event)}>
                          <Edit className="w-[18px] h-[18px]" strokeWidth={ADMIN_ICON_STROKE} />
                        </AdminTableIconButton>
                        <AdminTableIconButtonDanger title="Delete event" onClick={() => handleDeleteEvent(event.eventId)}>
                          <Trash2 className="w-[18px] h-[18px]" strokeWidth={ADMIN_ICON_STROKE} />
                        </AdminTableIconButtonDanger>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <AdminPaginationBar
          page={safeEventPage}
          pageSize={DEFAULT_ADMIN_PAGE_SIZE}
          total={filteredEventTotal}
          onPageChange={setListPage}
        />
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchEvents();
          }}
        />
      )}

      {showEditModal && selectedEvent && (
        <EditEventModal
          event={selectedEvent}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchEvents();
          }}
        />
      )}

      {showShowsModal && selectedEvent && (
        <ManageShowsModal
          event={selectedEvent}
          onClose={() => setShowShowsModal(false)}
          onSuccess={() => {
            setShowShowsModal(false);
            fetchEvents();
          }}
        />
      )}
    </div>
  );
};

/** Tags that switch event CTA from "Coming Soon" to "Contact us to book". */
const CONTACT_BOOKING_TAGS = [
  { value: 'coupon', hint: 'Swimming / coupon access events' },
  { value: 'on-demand', hint: 'Sailing / request-a-slot events' },
];

function EventTagsSection({ tags = [], onChange, idPrefix = 'event' }) {
  const [newTag, setNewTag] = useState('');

  const toggleContactTag = (tag) => {
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
    onChange(next);
  };

  const addCustomTag = () => {
    const t = newTag.trim().toLowerCase();
    if (!t || tags.includes(t)) {
      setNewTag('');
      return;
    }
    onChange([...tags, t]);
    setNewTag('');
  };

  const removeTag = (tag) => onChange(tags.filter((t) => t !== tag));

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Booking type tags</label>
        <p className="text-xs text-gray-500 mb-3">
          Check one below to show <strong>Contact us to book</strong> instead of Coming Soon (when ticket checkout is off).
        </p>
        <div className="space-y-2">
          {CONTACT_BOOKING_TAGS.map(({ value, hint }) => (
            <label key={value} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                id={`${idPrefix}-tag-${value}`}
                checked={tags.includes(value)}
                onChange={() => toggleContactTag(value)}
                className="mt-0.5 h-4 w-4 text-[#FF5E4C] focus:ring-[#FF5E4C]/30 border-[#E5E3DF] rounded"
              />
              <span className="text-sm text-gray-800">
                <span className="font-medium font-mono">{value}</span>
                <span className="text-gray-500"> — {hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-[#FAF7F2] border border-[#E5E3DF] px-2.5 py-1 text-xs text-gray-700"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-gray-400 hover:text-red-500"
                aria-label={`Remove tag ${tag}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustomTag();
            }
          }}
          placeholder="Other tag (optional)"
          className="flex-1 px-3 py-2 border border-[#E5E3DF] rounded-[12px] text-sm focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
        />
        <button
          type="button"
          onClick={addCustomTag}
          className="px-3 py-2 text-sm border border-[#E5E3DF] rounded-[12px] hover:bg-gray-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// Create Event Modal Component
const CreateEventModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'workshop',
    address: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    venue: '',
    startDate: '',
    endDate: '',
    duration: 120,
    rating: 0,
    ratingTag: '',
    founderVerified: false,
    inSpotlight: false,
    images: [],
    features: [],
    faqs: [],
    tags: [],
    capacity: {
      totalSeats: 100,
      maxPetsPerPerson: 2
    },
    pricing: {
      currency: 'INR',
      basePrice: 0,
      taxes: 0
    }
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    let previewUrls = [];
    try {
      setUploadingImages(true);
      previewUrls = files
        .filter((f) => f.type.startsWith('image/'))
        .map((file) => imageUtils.createPreviewUrl(file));
      if (previewUrls.length) setImagePreviewUrls((prev) => [...prev, ...previewUrls]);

      const { urls, errors } = await imageAPI.uploadGalleryMedia(files, 'events');
      if (errors.length) alert('Upload notes:\n' + errors.join('\n'));
      if (urls.length) {
        setFormData((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      } else if (errors.length) {
        throw new Error('No files uploaded');
      }

      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } catch (error) {
      console.error('Gallery upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload gallery media: ${details}` : 'Failed to upload gallery media.');
      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const reorderEventImages = (nextUrls) => {
    setFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const removeEventImage = async (index) => {
    const imageUrl = formData.images[index];
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (err) {
        console.error('Failed to delete image from storage:', err);
      }
    }
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for API - convert to ISO string for backend
      const eventData = {
        ...formData,
        images: formData.images.filter((img) => img && String(img).trim() !== ''),
        // Ensure dates are in ISO format for backend
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        // Convert empty latitude/longitude to null
        latitude: formData.latitude === '' ? null : (formData.latitude ? parseFloat(formData.latitude) : null),
        longitude: formData.longitude === '' ? null : (formData.longitude ? parseFloat(formData.longitude) : null)
      };

      // Remove empty optional fields to avoid validation errors
      if (!eventData.ratingTag || eventData.ratingTag.trim() === '') {
        delete eventData.ratingTag;
      }
      if (!eventData.features || eventData.features.length === 0) {
        delete eventData.features;
      }
      if (!eventData.faqs || eventData.faqs.length === 0) {
        delete eventData.faqs;
      }
      if (!eventData.tags || eventData.tags.length === 0) {
        delete eventData.tags;
      }

      console.log('Creating event with data:', eventData);
      
      const response = await eventsApi.createEvent(eventData);
      alert(`Event "${response.event.title}" created successfully!`);
      onSuccess();
    } catch (error) {
      console.error('Error creating event:', error);
      const errorMessage =
        error.response?.data?.details || error.response?.data?.error || 'Failed to create event';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-medium text-black">Create New Event</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                >
                  <option value="workshop">Workshop</option>
                  <option value="show">Show</option>
                  <option value="competition">Competition</option>
                  <option value="social">Social</option>
                  <option value="training">Training</option>
                  <option value="outdoor">Outdoor</option>
                  <option value="indoor">Indoor</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
              />
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue *
                </label>
                <input
                  type="text"
                  required
                  value={formData.venue}
                  onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Address *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
              />
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="e.g., 19.0760"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="e.g., 72.8777"
                />
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="30"
                  max="1440"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Event images</h2>

              <div className="border-2 border-dashed border-[#E5E3DF] rounded-[12px] p-6 mb-6 bg-[#FAF7F2]/40">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload event images</p>
                  <p className="text-sm text-gray-500 mb-4">10MB each. Supported: JPEG, PNG, WebP, GIF, HEIC</p>

                  {uploadingImages && (
                    <div className="mb-4">
                      <div className="inline-flex items-center px-4 py-2 bg-[#FF5E4C]/10 text-[#FF5E4C] rounded-[12px]">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#FF5E4C] border-t-transparent mr-2" />
                        Uploading images...
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    multiple
                    accept={imageUtils.galleryVideoAccept}
                    onChange={handleImageUpload}
                    className="hidden"
                    id="create-event-image-upload"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="create-event-image-upload"
                    className="inline-flex items-center px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 cursor-pointer transition-colors"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Choose images
                  </label>
                </div>
              </div>

              {(formData.images.length > 0 || imagePreviewUrls.length > 0) && (
                <div className="space-y-4">
                  <DraggableStayImagesGrid
                    images={formData.images}
                    onReorder={reorderEventImages}
                    onRemove={removeEventImage}
                    altPrefix="Event"
                    hintText="Drag to reorder. The first image is used as the event cover in listings and cards."
                    hintClassName="text-sm text-[#78736A] mb-3"
                  />
                  {imagePreviewUrls.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {imagePreviewUrls.map((previewUrl, index) => (
                        <div key={`preview-${index}`} className="relative group">
                          <img
                            src={previewUrl}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg opacity-50"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF5E4C] border-t-transparent" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Capacity and Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Capacity</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Seats
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.capacity.totalSeats}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        capacity: { ...prev.capacity, totalSeats: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Pets Per Person
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.capacity.maxPetsPerPerson}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        capacity: { ...prev.capacity, maxPetsPerPerson: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Base Pricing</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Price (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pricing.basePrice}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        pricing: {
                          ...prev.pricing,
                          basePrice: parseFloat(e.target.value) || 0,
                          taxes: roomTaxesFromDiscounted(parseFloat(e.target.value) || 0)
                        }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxes included (₹)
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={formData.pricing.taxes}
                      className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-700 rounded-lg cursor-not-allowed"
                      tabIndex={-1}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Options */}
            <EventTagsSection
              idPrefix="create"
              tags={formData.tags}
              onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))}
            />

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="founderVerified"
                  checked={formData.founderVerified}
                  onChange={(e) => setFormData(prev => ({ ...prev, founderVerified: e.target.checked }))}
                  className="h-4 w-4 text-[#FF5E4C] focus:ring-[#FF5E4C]/30 border-[#E5E3DF] rounded"
                />
                <label htmlFor="founderVerified" className="ml-2 block text-sm text-gray-900">
                  Founder Verified
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="inSpotlight"
                  checked={formData.inSpotlight}
                  onChange={(e) => setFormData(prev => ({ ...prev, inSpotlight: e.target.checked }))}
                  className="h-4 w-4 text-[#FF5E4C] focus:ring-[#FF5E4C]/30 border-[#E5E3DF] rounded"
                />
                <label htmlFor="inSpotlight" className="ml-2 block text-sm text-gray-900">
                  Feature in Spotlight
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Event Modal (similar to Create but with pre-filled data)
const EditEventModal = ({ event, onClose, onSuccess }) => {
  // Helper function to format datetime-local input value
  const formatDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    title: event.title || '',
    description: event.description || '',
    category: event.category || 'workshop',
    address: event.address || '',
    city: event.city || '',
    state: event.state || '',
    latitude: event.latitude != null ? event.latitude.toString() : '',
    longitude: event.longitude != null ? event.longitude.toString() : '',
    venue: event.venue || '',
    startDate: formatDateTimeLocal(event.startDate),
    endDate: formatDateTimeLocal(event.endDate),
    duration: event.duration || 120,
    rating: event.rating || 0,
    ratingTag: event.ratingTag || '',
    founderVerified: event.founderVerified || false,
    inSpotlight: event.inSpotlight || false,
    images: Array.isArray(event.images) && event.images.length > 0 ? [...event.images] : [],
    features: event.features || [],
    faqs: event.faqs || [],
    tags: event.tags || [],
    capacity: {
      totalSeats: event.capacity?.totalSeats || 100,
      maxPetsPerPerson: event.capacity?.maxPetsPerPerson || 2
    },
    pricing: {
      currency: event.pricing?.currency || 'INR',
      basePrice: event.pricing?.basePrice || 0,
      taxes: roomTaxesFromDiscounted(event.pricing?.basePrice || 0)
    }
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    let previewUrls = [];
    try {
      setUploadingImages(true);
      previewUrls = files
        .filter((f) => f.type.startsWith('image/'))
        .map((file) => imageUtils.createPreviewUrl(file));
      if (previewUrls.length) setImagePreviewUrls((prev) => [...prev, ...previewUrls]);

      const { urls, errors } = await imageAPI.uploadGalleryMedia(files, 'events');
      if (errors.length) alert('Upload notes:\n' + errors.join('\n'));
      if (urls.length) {
        setFormData((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      } else if (errors.length) {
        throw new Error('No files uploaded');
      }

      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } catch (error) {
      console.error('Gallery upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload gallery media: ${details}` : 'Failed to upload gallery media.');
      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const reorderEventImages = (nextUrls) => {
    setFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const removeEventImage = async (index) => {
    const imageUrl = formData.images[index];
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (err) {
        console.error('Failed to delete image from storage:', err);
      }
    }
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for API
      const eventData = {
        ...formData,
        images: formData.images.filter((img) => img && String(img).trim() !== ''),
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        // Convert empty latitude/longitude to null
        latitude: formData.latitude === '' ? null : (formData.latitude ? parseFloat(formData.latitude) : null),
        longitude: formData.longitude === '' ? null : (formData.longitude ? parseFloat(formData.longitude) : null)
      };

      // Remove empty optional fields to avoid validation errors
      if (!eventData.ratingTag || eventData.ratingTag.trim() === '') {
        delete eventData.ratingTag;
      }
      if (!eventData.features || eventData.features.length === 0) {
        delete eventData.features;
      }
      if (!eventData.faqs || eventData.faqs.length === 0) {
        delete eventData.faqs;
      }
      if (!eventData.tags || eventData.tags.length === 0) {
        delete eventData.tags;
      }

      console.log('Updating event with data:', eventData);
      
      const response = await eventsApi.updateEvent(event.eventId, eventData);
      alert(`Event "${formData.title}" updated successfully!`);
      onSuccess();
    } catch (error) {
      console.error('Error updating event:', error);
      const errorMessage =
        error.response?.data?.details || error.response?.data?.error || 'Failed to update event';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-medium text-black">Edit Event: {event.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                >
                  <option value="workshop">Workshop</option>
                  <option value="show">Show</option>
                  <option value="competition">Competition</option>
                  <option value="social">Social</option>
                  <option value="training">Training</option>
                  <option value="outdoor">Outdoor</option>
                  <option value="indoor">Indoor</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
              />
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue *
                </label>
                <input
                  type="text"
                  required
                  value={formData.venue}
                  onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Address *
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
              />
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="e.g., 19.0760"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="e.g., 72.8777"
                />
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="30"
                  max="1440"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                />
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Event images</h2>

              <div className="border-2 border-dashed border-[#E5E3DF] rounded-[12px] p-6 mb-6 bg-[#FAF7F2]/40">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload event images</p>
                  <p className="text-sm text-gray-500 mb-4">10MB each. Supported: JPEG, PNG, WebP, GIF, HEIC</p>

                  {uploadingImages && (
                    <div className="mb-4">
                      <div className="inline-flex items-center px-4 py-2 bg-[#FF5E4C]/10 text-[#FF5E4C] rounded-[12px]">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#FF5E4C] border-t-transparent mr-2" />
                        Uploading images...
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    multiple
                    accept={imageUtils.galleryVideoAccept}
                    onChange={handleImageUpload}
                    className="hidden"
                    id="edit-event-image-upload"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="edit-event-image-upload"
                    className="inline-flex items-center px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 cursor-pointer transition-colors"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Choose images
                  </label>
                </div>
              </div>

              {(formData.images.length > 0 || imagePreviewUrls.length > 0) && (
                <div className="space-y-4">
                  <DraggableStayImagesGrid
                    images={formData.images}
                    onReorder={reorderEventImages}
                    onRemove={removeEventImage}
                    altPrefix="Event"
                    hintText="Drag to reorder. The first image is used as the event cover in listings and cards."
                    hintClassName="text-sm text-[#78736A] mb-3"
                  />
                  {imagePreviewUrls.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {imagePreviewUrls.map((previewUrl, index) => (
                        <div key={`preview-${index}`} className="relative group">
                          <img
                            src={previewUrl}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg opacity-50"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF5E4C] border-t-transparent" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Capacity and Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Capacity</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Seats
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.capacity.totalSeats}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        capacity: { ...prev.capacity, totalSeats: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Pets Per Person
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.capacity.maxPetsPerPerson}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        capacity: { ...prev.capacity, maxPetsPerPerson: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Base Pricing</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Base Price (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pricing.basePrice}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        pricing: {
                          ...prev.pricing,
                          basePrice: parseFloat(e.target.value) || 0,
                          taxes: roomTaxesFromDiscounted(parseFloat(e.target.value) || 0)
                        }
                      }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxes included (₹)
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={formData.pricing.taxes}
                      className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-700 rounded-lg cursor-not-allowed"
                      tabIndex={-1}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Options */}
            <EventTagsSection
              idPrefix="edit"
              tags={formData.tags}
              onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))}
            />

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editFounderVerified"
                  checked={formData.founderVerified}
                  onChange={(e) => setFormData(prev => ({ ...prev, founderVerified: e.target.checked }))}
                  className="h-4 w-4 text-[#FF5E4C] focus:ring-[#FF5E4C]/30 border-[#E5E3DF] rounded"
                />
                <label htmlFor="editFounderVerified" className="ml-2 block text-sm text-gray-900">
                  Founder Verified
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editInSpotlight"
                  checked={formData.inSpotlight}
                  onChange={(e) => setFormData(prev => ({ ...prev, inSpotlight: e.target.checked }))}
                  className="h-4 w-4 text-[#FF5E4C] focus:ring-[#FF5E4C]/30 border-[#E5E3DF] rounded"
                />
                <label htmlFor="editInSpotlight" className="ml-2 block text-sm text-gray-900">
                  Feature in Spotlight
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Manage Shows Modal - Full Implementation
const ManageShowsModal = ({ event, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('shows');
  const [shows, setShows] = useState([]);
  const [selectedShow, setSelectedShow] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Show form states
  const [showForm, setShowForm] = useState({
    date: '',
    time: '',
    totalSeats: 100,
    venue: event.venue || ''
  });
  const [showFormErrors, setShowFormErrors] = useState({});
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  
  // Ticket form states
  const [ticketForm, setTicketForm] = useState({
    name: '',
    description: '',
    price: '',
    taxes: '',
    totalQuantity: 50
  });
  const [ticketFormErrors, setTicketFormErrors] = useState({});
  const [ticketFormLoading, setTicketFormLoading] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  
  // Confirmation dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [ticketDeleteConfirm, setTicketDeleteConfirm] = useState(null);

  // Fetch shows and tickets on mount
  useEffect(() => {
    fetchShows();
  }, []);

  const fetchShows = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching shows for event:', event.eventId);
      const response = await eventsApi.getEventShows(event.eventId);
      console.log('Shows fetched:', response);
      
      setShows(response || []);
      
      // Select first show by default
      if (response && response.length > 0) {
        setSelectedShow(response[0]);
        fetchTicketsForShow(response[0]);
      }
    } catch (err) {
      console.error('Error fetching shows:', err);
      setError(err.message || 'Failed to fetch shows');
      setShows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketsForShow = async (show) => {
    if (!show) return;
    
    try {
      console.log('Fetching tickets for show:', show.showId);
      const response = await eventsApi.getTicketTypes(event.eventId, show.showId);
      console.log('Tickets fetched:', response);
      
      setTickets(response || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setTickets([]);
    }
  };

  // Show management functions
  const handleCreateShow = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!showForm.date) errors.date = 'Date is required';
    if (!showForm.time) errors.time = 'Time is required';
    if (!showForm.totalSeats || showForm.totalSeats < 1) errors.totalSeats = 'Total seats must be at least 1';
    
    setShowFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setShowFormLoading(true);
      
      if (editingShow) {
        console.log('Updating show:', editingShow.showId, showForm);
        await eventsApi.updateShow(event.eventId, editingShow.showId, showForm);
      } else {
        console.log('Creating show:', showForm);
        await eventsApi.createShow(event.eventId, showForm);
      }
      
      // Reset form
      setShowForm({
        date: '',
        time: '',
        totalSeats: 100,
        venue: event.venue || ''
      });
      setEditingShow(null);
      
      // Refresh shows
      await fetchShows();
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving show:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save show');
    } finally {
      setShowFormLoading(false);
    }
  };

  const handleEditShow = (show) => {
    setEditingShow(show);
    setShowForm({
      date: show.date,
      time: show.time,
      totalSeats: show.totalSeats,
      venue: show.venue || event.venue || ''
    });
  };

  const handleDeleteShow = async (show) => {
    try {
      console.log('Deleting show:', show.showId);
      await eventsApi.deleteShow(event.eventId, show.showId);
      
      // Refresh shows
      await fetchShows();
      
      // Clear selection if deleted show was selected
      if (selectedShow?.showId === show.showId) {
        setSelectedShow(null);
        setTickets([]);
      }
      
      setShowDeleteConfirm(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error deleting show:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete show');
    }
  };

  // Ticket management functions
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!selectedShow) {
      setError('Please select a show first');
      return;
    }
    
    // Validate form
    const errors = {};
    if (!ticketForm.name) errors.name = 'Name is required';
    if (!ticketForm.price || ticketForm.price < 0) errors.price = 'Price must be 0 or greater';
    if (!ticketForm.totalQuantity || ticketForm.totalQuantity < 1) errors.totalQuantity = 'Quantity must be at least 1';
    
    setTicketFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setTicketFormLoading(true);
      
      const ticketPrice = parseInt(ticketForm.price);
      const ticketData = {
        ...ticketForm,
        price: ticketPrice,
        taxes: roomTaxesFromDiscounted(ticketPrice),
        totalQuantity: parseInt(ticketForm.totalQuantity)
      };
      
      if (editingTicket) {
        console.log('Updating ticket:', editingTicket.ticketTypeId, ticketData);
        await eventsApi.updateTicketType(event.eventId, selectedShow.showId, editingTicket.ticketTypeId, ticketData);
      } else {
        console.log('Creating ticket:', ticketData);
        await eventsApi.createTicketType(event.eventId, selectedShow.showId, ticketData);
      }
      
      // Reset form
      setTicketForm({
        name: '',
        description: '',
        price: '',
        taxes: '',
        totalQuantity: 50
      });
      setEditingTicket(null);
      
      // Refresh tickets
      await fetchTicketsForShow(selectedShow);
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving ticket:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save ticket type');
    } finally {
      setTicketFormLoading(false);
    }
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setTicketForm({
      name: ticket.name,
      description: ticket.description || '',
      price: ticket.price.amount.toString(),
      taxes: roomTaxesFromDiscounted(ticket.price.amount).toFixed(2),
      totalQuantity: ticket.totalQuantity
    });
  };

  const handleDeleteTicket = async (ticket) => {
    try {
      console.log('Deleting ticket:', ticket.ticketTypeId);
      await eventsApi.deleteTicketType(event.eventId, selectedShow.showId, ticket.ticketTypeId);
      
      // Refresh tickets
      await fetchTicketsForShow(selectedShow);
      
      setTicketDeleteConfirm(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error deleting ticket:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete ticket type');
    }
  };

  const formatDateTime = (date, time) => {
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">Manage Shows & Tickets</h2>
            <p className="text-sm text-gray-600">{event.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800 text-sm underline ml-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b px-6">
          <button
            onClick={() => setActiveTab('shows')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'shows'
                ? 'border-[#FF5E4C] text-[#FF5E4C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Shows ({shows.length})
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'tickets'
                ? 'border-[#FF5E4C] text-[#FF5E4C]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            disabled={!selectedShow}
          >
            Tickets ({selectedShow ? tickets.length : 0})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF5E4C] border-t-transparent"></div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              {activeTab === 'shows' && (
                <ShowsTab
                  shows={shows}
                  showForm={showForm}
                  setShowForm={setShowForm}
                  showFormErrors={showFormErrors}
                  showFormLoading={showFormLoading}
                  editingShow={editingShow}
                  setEditingShow={setEditingShow}
                  selectedShow={selectedShow}
                  setSelectedShow={setSelectedShow}
                  handleCreateShow={handleCreateShow}
                  handleEditShow={handleEditShow}
                  handleDeleteShow={handleDeleteShow}
                  setShowDeleteConfirm={setShowDeleteConfirm}
                  fetchTicketsForShow={fetchTicketsForShow}
                  formatDateTime={formatDateTime}
                />
              )}
              
              {activeTab === 'tickets' && (
                <TicketsTab
                  selectedShow={selectedShow}
                  tickets={tickets}
                  ticketForm={ticketForm}
                  setTicketForm={setTicketForm}
                  ticketFormErrors={ticketFormErrors}
                  ticketFormLoading={ticketFormLoading}
                  editingTicket={editingTicket}
                  setEditingTicket={setEditingTicket}
                  handleCreateTicket={handleCreateTicket}
                  handleEditTicket={handleEditTicket}
                  handleDeleteTicket={handleDeleteTicket}
                  setTicketDeleteConfirm={setTicketDeleteConfirm}
                />
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmations */}
        {showDeleteConfirm && (
          <ConfirmDialog
            title="Delete Show"
            message={`Are you sure you want to delete the show on ${showDeleteConfirm.date} at ${showDeleteConfirm.time}? This will also delete all associated ticket types.`}
            onConfirm={() => handleDeleteShow(showDeleteConfirm)}
            onCancel={() => setShowDeleteConfirm(null)}
            confirmText="Delete"
            confirmColor="red"
          />
        )}

        {ticketDeleteConfirm && (
          <ConfirmDialog
            title="Delete Ticket Type"
            message={`Are you sure you want to delete the "${ticketDeleteConfirm.name}" ticket type?`}
            onConfirm={() => handleDeleteTicket(ticketDeleteConfirm)}
            onCancel={() => setTicketDeleteConfirm(null)}
            confirmText="Delete"
            confirmColor="red"
          />
        )}
      </div>
    </div>
  );
};

// Shows Tab Component
const ShowsTab = ({
  shows,
  showForm,
  setShowForm,
  showFormErrors,
  showFormLoading,
  editingShow,
  setEditingShow,
  selectedShow,
  setSelectedShow,
  handleCreateShow,
  handleEditShow,
  setShowDeleteConfirm,
  fetchTicketsForShow,
  formatDateTime
}) => {
  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shows List */}
        <div>
          <h3 className="text-lg font-medium mb-4">Existing Shows</h3>
          
          {shows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No shows created yet.</p>
              <p className="text-sm">Create your first show using the form →</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {shows.map((show) => (
                <div
                  key={show.showId}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedShow?.showId === show.showId
                      ? 'border-[#FF5E4C] bg-[#FAF7F2]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedShow(show);
                    fetchTicketsForShow(show);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {formatDateTime(show.date, show.time)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {show.availableSeats}/{show.totalSeats} seats available
                      </p>
                      {show.venue && (
                        <p className="text-sm text-gray-500">{show.venue}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4 shrink-0">
                      <AdminTableIconButton
                        title="Edit show"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditShow(show);
                        }}
                      >
                        <Edit className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButton>
                      <AdminTableIconButtonDanger
                        title="Delete show"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(show);
                        }}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButtonDanger>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Show Form */}
        <div>
          <h3 className="text-lg font-medium mb-4">
            {editingShow ? 'Edit Show' : 'Add New Show'}
          </h3>
          
          <form onSubmit={handleCreateShow} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={showForm.date}
                onChange={(e) => setShowForm(prev => ({ ...prev, date: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                  showFormErrors.date ? 'border-red-300' : 'border-gray-300'
                }`}
                min={new Date().toISOString().split('T')[0]}
              />
              {showFormErrors.date && (
                <p className="text-red-600 text-sm mt-1">{showFormErrors.date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                type="time"
                value={showForm.time}
                onChange={(e) => setShowForm(prev => ({ ...prev, time: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                  showFormErrors.time ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {showFormErrors.time && (
                <p className="text-red-600 text-sm mt-1">{showFormErrors.time}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Seats *
              </label>
              <input
                type="number"
                min="1"
                value={showForm.totalSeats}
                onChange={(e) => setShowForm(prev => ({ ...prev, totalSeats: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                  showFormErrors.totalSeats ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {showFormErrors.totalSeats && (
                <p className="text-red-600 text-sm mt-1">{showFormErrors.totalSeats}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Venue (Optional)
              </label>
              <input
                type="text"
                value={showForm.venue}
                onChange={(e) => setShowForm(prev => ({ ...prev, venue: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                placeholder="Leave empty to use event venue"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={showFormLoading}
                className="flex-1 bg-[#FF5E4C] text-white py-2 px-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {showFormLoading ? 'Saving...' : (editingShow ? 'Update Show' : 'Create Show')}
              </button>
              
              {editingShow && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingShow(null);
                    setShowForm({
                      date: '',
                      time: '',
                      totalSeats: 100,
                      venue: ''
                    });
                  }}
                  className="px-4 py-2 text-[#5c5c5c] border border-[#E5E3DF] bg-white rounded-full hover:bg-[#FAF7F2] transition-colors text-[14px] font-medium"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Tickets Tab Component
const TicketsTab = ({
  selectedShow,
  tickets,
  ticketForm,
  setTicketForm,
  ticketFormErrors,
  ticketFormLoading,
  editingTicket,
  setEditingTicket,
  handleCreateTicket,
  handleEditTicket,
  setTicketDeleteConfirm
}) => {
  if (!selectedShow) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">No Show Selected</p>
          <p className="text-sm">Please select a show from the Shows tab to manage its ticket types.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium">Tickets for Show</h3>
        <p className="text-sm text-gray-600">
          {new Date(`${selectedShow.date}T${selectedShow.time}`).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })}
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets List */}
        <div>
          <h4 className="font-medium mb-4">Existing Ticket Types</h4>
          
          {tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No ticket types created yet.</p>
              <p className="text-sm">Create your first ticket type using the form →</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {tickets.map((ticket) => (
                <div key={ticket.ticketTypeId} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900">{ticket.name}</h5>
                      {ticket.description && (
                        <p className="text-sm text-gray-600 mt-1">{ticket.description}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        <p className="text-sm">
                          <span className="font-medium">Price:</span> ₹{ticket.price.amount.toLocaleString()}
                          {roomTaxesFromDiscounted(ticket.price.amount) > 0 && ` (includes ₹${roomTaxesFromDiscounted(ticket.price.amount).toLocaleString()} taxes)`}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Available:</span> {ticket.availableQuantity}/{ticket.totalQuantity}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4 shrink-0">
                      <AdminTableIconButton title="Edit ticket" onClick={() => handleEditTicket(ticket)}>
                        <Edit className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButton>
                      <AdminTableIconButtonDanger title="Delete ticket" onClick={() => setTicketDeleteConfirm(ticket)}>
                        <Trash2 className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                      </AdminTableIconButtonDanger>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Ticket Form */}
        <div>
          <h4 className="font-medium mb-4">
            {editingTicket ? 'Edit Ticket Type' : 'Add New Ticket Type'}
          </h4>
          
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ticket Name *
              </label>
              <input
                type="text"
                value={ticketForm.name}
                onChange={(e) => setTicketForm(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                  ticketFormErrors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., General Admission, VIP, Family Pack"
              />
              {ticketFormErrors.name && (
                <p className="text-red-600 text-sm mt-1">{ticketFormErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                rows="3"
                placeholder="Brief description of what's included..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (₹) *
                </label>
                <input
                  type="number"
                  min="0"
                  value={ticketForm.price}
                  onChange={(e) => {
                    const amount = parseFloat(e.target.value) || 0;
                    setTicketForm(prev => ({
                      ...prev,
                      price: e.target.value,
                      taxes: amount ? roomTaxesFromDiscounted(amount).toFixed(2) : ''
                    }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                    ticketFormErrors.price ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {ticketFormErrors.price && (
                  <p className="text-red-600 text-sm mt-1">{ticketFormErrors.price}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taxes included (₹)
                </label>
                <input
                  type="text"
                  readOnly
                  value={ticketForm.taxes}
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-700 rounded-lg cursor-not-allowed"
                  tabIndex={-1}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Quantity *
              </label>
              <input
                type="number"
                min="1"
                value={ticketForm.totalQuantity}
                onChange={(e) => setTicketForm(prev => ({ ...prev, totalQuantity: parseInt(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] ${
                  ticketFormErrors.totalQuantity ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {ticketFormErrors.totalQuantity && (
                <p className="text-red-600 text-sm mt-1">{ticketFormErrors.totalQuantity}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={ticketFormLoading}
                className="flex-1 bg-[#FF5E4C] text-white py-2 px-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {ticketFormLoading ? 'Saving...' : (editingTicket ? 'Update Ticket' : 'Create Ticket')}
              </button>
              
              {editingTicket && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingTicket(null);
                    setTicketForm({
                      name: '',
                      description: '',
                      price: '',
                      taxes: '',
                      totalQuantity: 50
                    });
                  }}
                  className="px-4 py-2 text-[#5c5c5c] border border-[#E5E3DF] bg-white rounded-full hover:bg-[#FAF7F2] transition-colors text-[14px] font-medium"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmText = 'Confirm', confirmColor = 'blue' }) => {
  const confirmButtonClass = confirmColor === 'red' 
    ? 'bg-red-600 hover:bg-red-700' 
    : 'bg-[#FF5E4C] hover:opacity-90';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-[16px] border border-[#E5E3DF] shadow-lg max-w-md w-full p-6">
        <h3 className="text-[17px] font-medium text-black mb-2">{title}</h3>
        <p className="text-[14px] text-[#78736A] mb-6 leading-relaxed">{message}</p>
        
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[#5c5c5c] border border-[#E5E3DF] bg-white rounded-full hover:bg-[#FAF7F2] transition-colors text-[14px] font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded-full transition-opacity ${confirmButtonClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEvents;
